import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Grid, Paper, Typography, Box, Card, CardContent,
  LinearProgress, Chip, IconButton, useTheme, alpha, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  useMediaQuery
} from '@mui/material';
import {
  ConfirmationNumber, CheckCircle, Schedule, Warning,
  TrendingUp, TrendingDown, MoreVert, ArrowForward,
  Assignment, AccessTime, Business, Person, Lock
} from '@mui/icons-material';
import { ticketService } from '../services/ticketService';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const Dashboard = () => {
  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    inProgress: 0,
    resolved: 0,
    totalTrend: null,
    openTrend: null,
    inProgressTrend: null,
    resolvedTrend: null,
    resolutionRate: 0
  });
  const [recentTickets, setRecentTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employeeHours, setEmployeeHours] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeDetails, setEmployeeDetails] = useState([]);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [adminStats, setAdminStats] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isAdmin = user?.role === 'admin';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    loadDashboardData();
    loadEmployeeHours();
    if (isAdmin) loadAdminStats();
  }, [isAdmin]);

  const loadAdminStats = async () => {
    try {
      const data = await ticketService.getStats();
      setAdminStats(data);
    } catch (error) {
      console.error('Failed to load admin stats:', error);
      setAdminStats(null);
    }
  };

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const tickets = await ticketService.getAllTickets();

      const now = new Date();
      const startOfThisWeek = new Date(now);
      startOfThisWeek.setDate(now.getDate() - now.getDay() + (now.getDay() === 0 ? -6 : 1));
      startOfThisWeek.setHours(0, 0, 0, 0);
      const startOfLastWeek = new Date(startOfThisWeek);
      startOfLastWeek.setDate(startOfLastWeek.getDate() - 7);

      const isResolved = (s) => ['abgerechnet', 'resolved', 'closed', 'Abgerechnet'].includes(s);
      const isOpen = (s) => s === 'open';
      const isInProgress = (s) => s === 'in_progress' || s === 'in progress';

      const thisWeekTickets = tickets.filter(t => new Date(t.created_at) >= startOfThisWeek);
      const lastWeekTickets = tickets.filter(t => {
        const d = new Date(t.created_at);
        return d >= startOfLastWeek && d < startOfThisWeek;
      });

      const calcTrend = (thisCount, lastCount) => {
        if (lastCount === 0) return thisCount > 0 ? 100 : null;
        return Math.round(((thisCount - lastCount) / lastCount) * 100);
      };

      const total = tickets.length;
      const open = tickets.filter(t => isOpen(t.status)).length;
      const inProgress = tickets.filter(t => isInProgress(t.status)).length;
      const resolved = tickets.filter(t => isResolved(t.status)).length;

      const resolutionRate = total > 0 ? Math.round((resolved / total) * 100) : 0;

      const statsData = {
        total,
        open,
        inProgress,
        resolved,
        totalTrend: calcTrend(thisWeekTickets.length, lastWeekTickets.length),
        openTrend: calcTrend(
          thisWeekTickets.filter(t => isOpen(t.status)).length,
          lastWeekTickets.filter(t => isOpen(t.status)).length
        ),
        inProgressTrend: calcTrend(
          thisWeekTickets.filter(t => isInProgress(t.status)).length,
          lastWeekTickets.filter(t => isInProgress(t.status)).length
        ),
        resolvedTrend: calcTrend(
          thisWeekTickets.filter(t => isResolved(t.status)).length,
          lastWeekTickets.filter(t => isResolved(t.status)).length
        ),
        resolutionRate
      };

      setStats(statsData);
      setRecentTickets(tickets.slice(0, 5));
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployeeHours = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/dashboard/employee-hours', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployeeHours(response.data);
    } catch (error) {
      console.error('Failed to load employee hours:', error);
      setEmployeeHours([]);
    }
  };

  const handleEmployeeHourClick = async (employee) => {
    if (!isAdmin && employee.username !== user?.username) {
      return;
    }
    setSelectedEmployee(employee);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/dashboard/employee-hours/${employee.id}/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployeeDetails(response.data);
    } catch (error) {
      console.error('Failed to load employee details:', error);
      setEmployeeDetails([]);
    }
    setDetailDialogOpen(true);
  };

  const StatCard = ({ title, value, icon, color, trend = null }) => (
    <Card 
      sx={{ 
        height: '100%',
        background: `linear-gradient(135deg, ${alpha(color, 0.1)} 0%, ${alpha(color, 0.05)} 100%)`,
        border: `1px solid ${alpha(color, 0.2)}`,
        boxShadow: 'none',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: `0 20px 40px -12px ${alpha(color, 0.3)}`,
          border: `1px solid ${alpha(color, 0.3)}`,
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: `linear-gradient(90deg, ${color} 0%, ${alpha(color, 0.6)} 100%)`,
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            <Typography 
              variant="caption" 
              sx={{ 
                color: theme.palette.text.secondary,
                fontWeight: 600,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                fontSize: '0.75rem'
              }}
            >
              {title}
            </Typography>
            <Typography 
              variant="h3" 
              component="h2"
              sx={{
                fontWeight: 700,
                mt: 1,
                mb: 1,
                background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.8)} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {value}
            </Typography>
            {trend && (
              <Box display="flex" alignItems="center" gap={0.5}>
                {trend > 0 ? (
                  <TrendingUp sx={{ fontSize: 16, color: theme.palette.success.main }} />
                ) : (
                  <TrendingDown sx={{ fontSize: 16, color: theme.palette.error.main }} />
                )}
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: trend > 0 ? theme.palette.success.main : theme.palette.error.main,
                    fontWeight: 600
                  }}
                >
                  {Math.abs(trend)}% diese Woche
                </Typography>
              </Box>
            )}
          </Box>
          <Box 
            sx={{ 
              p: 1.5,
              borderRadius: '12px',
              backgroundColor: alpha(color, 0.1),
              color: color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {React.cloneElement(icon, { sx: { fontSize: 28 } })}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
      {loading && (
        <LinearProgress 
          sx={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            zIndex: 1500 
          }} 
        />
      )}
      
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700,
                background: 'linear-gradient(135deg, #CC0000 0%, #8B0000 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Willkommen zurück, {user?.username}!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Hier ist deine Übersicht für heute
            </Typography>
          </Box>
          <Chip 
            label="Live" 
            color="success" 
            size="small"
            icon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#10b981', mr: -0.5 }} />}
            sx={{ fontWeight: 600 }}
          />
        </Box>
      </Box>
      
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Gesamt Tickets"
            value={stats.total}
            icon={<ConfirmationNumber />}
            color={theme.palette.primary.main}
            trend={stats.totalTrend}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Offene Tickets"
            value={stats.open}
            icon={<Warning />}
            color={theme.palette.warning.main}
            trend={stats.openTrend}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="In Bearbeitung"
            value={stats.inProgress}
            icon={<Schedule />}
            color={theme.palette.info.main}
            trend={stats.inProgressTrend}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Abgeschlossen"
            value={stats.resolved}
            icon={<CheckCircle />}
            color={theme.palette.success.main}
            trend={stats.resolvedTrend}
          />
        </Grid>
      </Grid>

      {isAdmin && adminStats && (
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard title="Zugewiesen" value={adminStats.counts.assigned} icon={<Assignment />} color={theme.palette.info.main} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard title="Nicht zugewiesen" value={adminStats.counts.unassigned} icon={<Warning />} color={theme.palette.warning.main} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard title="Ø Bearbeitungszeit" value={adminStats.avgResolutionDays != null ? `${adminStats.avgResolutionDays.toFixed(1)} T` : '—'} icon={<AccessTime />} color={theme.palette.secondary.main} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <StatCard title="Erledigt" value={adminStats.counts.done} icon={<CheckCircle />} color={theme.palette.success.main} />
            </Grid>
          </Grid>

          <Paper sx={{ p: 3, mb: 4, borderRadius: '16px', boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)' }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Person sx={{ color: theme.palette.primary.main }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Auslastung je Techniker (aktive Tickets)</Typography>
            </Box>
            {adminStats.workload.length === 0 ? (
              <Typography color="textSecondary">Keine Daten</Typography>
            ) : (
              <Box display="flex" flexDirection="column" gap={1.5}>
                {adminStats.workload.map((w) => {
                  const max = Math.max(1, ...adminStats.workload.map(x => x.active_tickets));
                  return (
                    <Box key={w.id} display="flex" alignItems="center" gap={2}>
                      <Typography variant="body2" sx={{ minWidth: { xs: 110, sm: 180 } }} noWrap>{w.username}</Typography>
                      <Box flex={1}>
                        <LinearProgress variant="determinate" value={(w.active_tickets / max) * 100} sx={{ height: 10, borderRadius: 5, backgroundColor: alpha(theme.palette.primary.main, 0.1) }} />
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 32, textAlign: 'right' }}>{w.active_tickets}</Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
        </>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Paper 
            sx={{ 
              p: 0,
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
              background: '#ffffff',
            }}
          >
            <Box 
              sx={{ 
                p: 3,
                background: 'linear-gradient(135deg, #CC0000 0%, #8B0000 100%)',
                color: '#ffffff'
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box display="flex" alignItems="center" gap={1}>
                  <Assignment />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Aktuelle Tickets
                  </Typography>
                  <Chip 
                    label={`${recentTickets.length} neu`} 
                    size="small" 
                    sx={{ 
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      color: '#ffffff',
                      fontWeight: 600
                    }}
                  />
                </Box>
                <IconButton 
                  size="small" 
                  onClick={() => navigate('/tickets')}
                  sx={{ 
                    color: '#ffffff',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)'
                    }
                  }}
                >
                  <ArrowForward />
                </IconButton>
              </Box>
            </Box>
            
            {recentTickets.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <ConfirmationNumber sx={{ fontSize: 48, color: theme.palette.text.secondary, opacity: 0.3 }} />
                <Typography color="textSecondary" sx={{ mt: 2 }}>
                  Keine aktuellen Tickets vorhanden
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Neue Tickets werden hier angezeigt
                </Typography>
              </Box>
            ) : (
              <Box>
                {recentTickets.map((ticket, index) => {
                  const statusColors = {
                    open: theme.palette.warning.main,
                    in_progress: theme.palette.info.main,
                    'in progress': theme.palette.info.main,
                    abgerechnet: theme.palette.success.main,
                    Abgerechnet: theme.palette.success.main,
                    resolved: theme.palette.success.main,
                    closed: theme.palette.text.secondary
                  };
                  
                  const priorityColors = {
                    low: theme.palette.success.main,
                    medium: theme.palette.warning.main,
                    high: theme.palette.error.main,
                    urgent: theme.palette.error.dark
                  };
                  
                  return (
                    <Box
                      key={ticket.id}
                      onClick={() => navigate(`/tickets/${ticket.id}`)}
                      sx={{
                        p: 2.5,
                        borderBottom: index < recentTickets.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.04),
                          paddingLeft: 3,
                        },
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box flex={1}>
                          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: theme.palette.text.secondary,
                                fontWeight: 600
                              }}
                            >
                              #{ticket.id}
                            </Typography>
                            <Chip 
                              label={ticket.status.replace('_', ' ').toUpperCase()} 
                              size="small"
                              sx={{ 
                                backgroundColor: alpha(statusColors[ticket.status] || '#666', 0.1),
                                color: statusColors[ticket.status] || '#666',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                height: '20px'
                              }}
                            />
                            <Chip 
                              label={ticket.priority?.toUpperCase() || 'NORMAL'} 
                              size="small"
                              sx={{ 
                                backgroundColor: alpha(priorityColors[ticket.priority] || '#666', 0.1),
                                color: priorityColors[ticket.priority] || '#666',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                height: '20px'
                              }}
                            />
                          </Box>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: 500,
                              color: theme.palette.text.primary,
                              mb: 0.5
                            }}
                          >
                            {ticket.title}
                          </Typography>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <AccessTime sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
                              <Typography variant="caption" color="textSecondary">
                                {new Date(ticket.created_at).toLocaleDateString('de-DE')}
                              </Typography>
                            </Box>
                            {ticket.assignee && (
                              <Typography variant="caption" color="textSecondary">
                                Zugewiesen an: {ticket.assignee}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <IconButton size="small">
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
        </Grid>
        
        <Grid item xs={12} lg={4}>
          <Paper 
            sx={{ 
              p: 3,
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #555555 0%, #333333 100%)',
              color: '#ffffff',
              mb: 3
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <TrendingUp />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Leistungsübersicht
              </Typography>
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
              {stats.resolutionRate}%
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Lösungsrate gesamt
            </Typography>
            <LinearProgress
              variant="determinate"
              value={stats.resolutionRate} 
              sx={{ 
                mt: 2,
                height: 8,
                borderRadius: 4,
                backgroundColor: 'rgba(255,255,255,0.3)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#ffffff',
                  borderRadius: 4
                }
              }}
            />
          </Paper>
          
          <Paper 
            sx={{ 
              p: 3,
              borderRadius: '16px',
              boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Schnellaktionen
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Card 
                  onClick={() => navigate('/tickets/new')}
                  sx={{ 
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: alpha(theme.palette.primary.main, 0.05),
                    boxShadow: 'none',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 10px 20px -5px rgba(204, 0, 0, 0.2)',
                      background: alpha(theme.palette.primary.main, 0.1),
                    }
                  }}
                >
                  <ConfirmationNumber sx={{ color: theme.palette.primary.main, mb: 1 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    Neues Ticket
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card 
                  onClick={() => navigate('/kc-management')}
                  sx={{ 
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: alpha(theme.palette.secondary.main, 0.05),
                    boxShadow: 'none',
                    border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 10px 20px -5px rgba(85, 85, 85, 0.2)',
                      background: alpha(theme.palette.secondary.main, 0.1),
                    }
                  }}
                >
                  <Business sx={{ color: theme.palette.secondary.main, mb: 1 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    KC verwalten
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Planned Work Hours Section */}
      <Paper
        sx={{
          mt: 4,
          p: 3,
          borderRadius: '16px',
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
        }}
      >
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3
        }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Schedule sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Geplante Arbeitsstunden pro Mitarbeiter
            </Typography>
            {!isAdmin && (
              <Tooltip title="Als Benutzer können Sie nur Ihre eigenen Daten einsehen">
                <Lock sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
              </Tooltip>
            )}
          </Box>
          <Chip
            label={employeeHours[0]?.weekNumber || 'KW 39'}
            size="small"
            sx={{ fontWeight: 600 }}
          />
        </Box>

        {isMobile ? (
          <Box display="flex" flexDirection="column" gap={1}>
            {employeeHours.map((employee) => {
              const isOwnData = employee.username === user?.username;
              const canViewDetails = isAdmin || isOwnData;
              return (
                <Card
                  key={employee.id}
                  onClick={() => canViewDetails && handleEmployeeHourClick(employee)}
                  sx={{
                    cursor: canViewDetails ? 'pointer' : 'default',
                    opacity: !isAdmin && !isOwnData ? 0.6 : 1,
                    '&:active': canViewDetails ? { transform: 'scale(0.98)' } : {},
                    transition: 'transform 0.1s',
                    border: isOwnData ? `1px solid ${alpha(theme.palette.primary.main, 0.3)}` : undefined,
                  }}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box display="flex" alignItems="center" gap={1}>
                        <Person sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                        <Typography variant="body2" sx={{ fontWeight: isOwnData ? 600 : 400 }}>
                          {employee.username}
                        </Typography>
                        {isOwnData && <Chip label="Sie" size="small" sx={{ height: 20, fontSize: '0.7rem' }} color="primary" />}
                      </Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        {(isAdmin || isOwnData) ? (
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{employee.actualHours}h</Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                        {canViewDetails && <ArrowForward sx={{ fontSize: 16, color: theme.palette.text.secondary }} />}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Mitarbeiter</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Stunden</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Aktion</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {employeeHours.map((employee) => {
                  const isOwnData = employee.username === user?.username;
                  const canViewDetails = isAdmin || isOwnData;
                  return (
                    <TableRow key={employee.id} sx={{ cursor: canViewDetails ? 'pointer' : 'default', opacity: !isAdmin && !isOwnData ? 0.6 : 1, '&:hover': canViewDetails ? { backgroundColor: alpha(theme.palette.primary.main, 0.04) } : {} }} onClick={() => canViewDetails && handleEmployeeHourClick(employee)}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Person sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                          <Typography variant="body2" component="span" sx={{ fontWeight: isOwnData ? 600 : 400 }}>
                            {employee.username}
                          </Typography>
                          {isOwnData && <Chip label="Sie" size="small" sx={{ height: 20, fontSize: '0.7rem' }} color="primary" />}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        {(isAdmin || isOwnData) ? <Typography variant="body2" sx={{ fontWeight: 600 }}>{employee.actualHours}h</Typography> : <Typography variant="body2" color="text.secondary">-</Typography>}
                      </TableCell>
                      <TableCell align="center">
                        {canViewDetails ? (
                          <Tooltip title={isOwnData ? "Ihre Details anzeigen" : "Details anzeigen"}>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEmployeeHourClick(employee); }}><ArrowForward fontSize="small" /></IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Keine Berechtigung"><Lock sx={{ fontSize: 16, color: theme.palette.text.secondary }} /></Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!isAdmin && (
          <Box sx={{
            mt: 2,
            p: 2,
            backgroundColor: alpha(theme.palette.info.main, 0.1),
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Lock sx={{ fontSize: 18, color: theme.palette.info.main }} />
            <Typography variant="caption" color="text.secondary">
              Sie können nur Ihre eigenen Arbeitsstunden einsehen und bearbeiten.
              Die Daten anderer Mitarbeiter sind für Sie nicht sichtbar.
            </Typography>
          </Box>
        )}
      </Paper>

      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Person />
            Arbeitsstunden — {selectedEmployee?.username}
            <Chip label={selectedEmployee?.weekNumber} size="small" sx={{ fontWeight: 600 }} />
          </Box>
        </DialogTitle>
        <DialogContent>
          {employeeDetails.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="textSecondary">
                Keine Arbeitsstunden in dieser Woche erfasst.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Datum</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Ticket</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Stunden</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Beschreibung</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employeeDetails.map((entry, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        {new Date(entry.date).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' })}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`#${entry.ticket_id} — ${entry.ticket_title || 'Ticket'}`}
                          size="small"
                          variant="outlined"
                          onClick={() => { setDetailDialogOpen(false); navigate(`/tickets/${entry.ticket_id}`); }}
                          sx={{ cursor: 'pointer' }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{entry.hours}h</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{entry.description || '—'}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={2} sx={{ fontWeight: 700 }}>Gesamt</TableCell>
                    <TableCell align="center">
                      <Typography sx={{ fontWeight: 700 }}>
                        {employeeDetails.reduce((sum, e) => sum + parseFloat(e.hours), 0)}h
                      </Typography>
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Dashboard;